// src/components/suppliers/SuppliersTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetSuppliers from "../../hooks/suppliers/useGetSuppliers";
import useDeleteSupplier from "../../hooks/suppliers/useDeleteSupplier";
import useDeleteAllSuppliers from "../../hooks/suppliers/useDeleteAllSuppliers";
import useRestoreSupplier from "../../hooks/suppliers/useRestoreSupplier";
import useRestoreAllSuppliers from "../../hooks/suppliers/useRestoreAllSuppliers";
import useForceDeleteSupplier from "../../hooks/suppliers/useForceDeleteSupplier";
import useForceDeleteAllSuppliers from "../../hooks/suppliers/useForceDeleteAllSuppliers";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import SuppliersTableRow from "./Table/SuppliersTableRow";
import SuppliersTableColumns from "./Table/SuppliersTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const SuppliersTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("suppliers_list"),
            create: has("suppliers_create"),
            edit: has("suppliers_edit"),
            delete: has("suppliers_delete"),
            forceDelete: has("suppliers_force_delete"),
            archiveList: has("suppliers_archive_list"),
            restore: has("suppliers_restore"),
            restoreAll: has("suppliers_restore-all-table"),
            deleteAll: has("suppliers_delete-all-table"),
            massForceDeleteAll: has("suppliers_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/suppliers/archive");

    // CRUD Hooks
    const { deleteSupplierMutate, isPending: isDelete } = useDeleteSupplier();
    const { deleteAllSuppliersMutate, isPending: isDeleteAll } = useDeleteAllSuppliers();
    const { restoreSupplierMutate, isPending: isRestore } = useRestoreSupplier();
    const { restoreAllSuppliersMutate, isPending: isRestoreAll } = useRestoreAllSuppliers();
    const { forceDeleteSupplierMutate, isPending: isForceDelete } = useForceDeleteSupplier();
    const { forceDeleteAllSuppliersMutate, isPending: isForceDeleteAll } = useForceDeleteAllSuppliers();

    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/suppliers",
        deleteSingle: deleteSupplierMutate,
        deleteAll: deleteAllSuppliersMutate,
        restoreSingle: restoreSupplierMutate,
        restoreAll: restoreAllSuppliersMutate,
        forceDeleteSingle: forceDeleteSupplierMutate,
        forceDeleteAll: forceDeleteAllSuppliersMutate,
        isArchive,
    });

    const filters = useMemo(
        () => ({
            search: search || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, sortBy, sortDir, isArchive]
    );

    const { isLoading, error, suppliers, meta } = useGetSuppliers(page, perPage, filters);

    const items = useMemo(() => suppliers?.items || [], [suppliers]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <SuppliersTableRow
            key={params.item.id}
            supplier={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "الموردين" : "Suppliers";
    const subtitle = isArchive
        ? isArabic
            ? "عرض الموردين المحذوفين (الأرشيف)"
            : "Archived suppliers (soft deleted)"
        : isArabic
            ? "إدارة الموردين المتاحين في النظام"
            : "Manage available suppliers in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة مورد"
            : "Create new supplier";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllSuppliersMutate({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/suppliers/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/suppliers/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/suppliers"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={SuppliersTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد موردين في الأرشيف" : "لا توجد موردين"}
                emptyLabelEn={isArchive ? "No archived suppliers" : "No suppliers"}
                loadErrorAr="حدث خطأ أثناء تحميل الموردين"
                loadErrorEn="Failed to load suppliers"
            />
        </>
    );
};

export default SuppliersTable;
